import type {ReactNode} from 'react'
import {Link} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {Tag} from '@/components/ui/tag'
import type {ValidacionMasiva} from '@/hooks/bateria/use-bateria'

/** Único para las tres puertas que validan en bloque (mesa de trabajo, lote y cierre de la cola).
 * El informe lo encadena el servidor en `POST .../validar`; aquí solo se cuenta. */
export function ValidacionResultado({
  resultado,
  nombreDePaquete,
  arreglarHref,
  accionFaltanDudas,
}: {
  resultado: ValidacionMasiva
  nombreDePaquete: (paqueteId: string) => string
  arreglarHref: string
  accionFaltanDudas?: ReactNode
}) {
  const {t} = useTranslation()

  return (
    <div className="space-y-2" data-testid="validacion-resultado">
      <p className="text-sm text-foreground" data-testid="validacion-validados">
        {t('admin.bateria.validacion.validados', {count: resultado.validados})}
      </p>
      {resultado.bloqueados.length > 0 && (
        <ul className="space-y-1.5" data-testid="validacion-bloqueados">
          {resultado.bloqueados.map((bloqueado) => (
            <li key={bloqueado.paquete_id} className="flex flex-wrap items-center gap-2 text-sm">
              <Tag tone="amber">{t(`admin.bateria.validacion.bloqueo.${bloqueado.motivo}`)}</Tag>
              <span className="text-foreground">{nombreDePaquete(bloqueado.paquete_id)}</span>
              {bloqueado.campos_faltantes.length > 0 && (
                <span
                  className="text-xs text-muted-foreground"
                  data-testid={`validacion-bloqueado-campos-${bloqueado.paquete_id}`}>
                  {bloqueado.campos_faltantes.map((campo) => t(`admin.bateria.ficha.campo.${campo}`)).join(' · ')}
                </span>
              )}
              <Link
                to={arreglarHref}
                className="text-xs text-primary hover:underline"
                data-testid={`validacion-bloqueado-${bloqueado.paquete_id}`}>
                {t('admin.bateria.validacion.arreglar')}
              </Link>
            </li>
          ))}
        </ul>
      )}
      {/* En trámite, no escalón de magnitud (U2): el lime es de la rampa de riesgo. */}
      {resultado.informe_lanzado && (
        <p className="tone-en-curso rounded-lg px-4 py-3 text-sm" data-testid="validacion-informe-generando">
          {t('admin.bateria.validacion.informeGenerando')}
        </p>
      )}
      {/* Con dudas vivas el informe no arranca (guarda del runbook): es trabajo pendiente, no un error. */}
      {!resultado.informe_lanzado && resultado.validados > 0 && resultado.dudas_pendientes > 0 && (
        <div
          className="notice-amber flex flex-wrap items-center justify-between gap-2 rounded-lg px-4 py-3 text-sm"
          data-testid="validacion-informe-faltan-dudas">
          <span>{t('admin.bateria.validacion.informeFaltanDudas', {pendientes: resultado.dudas_pendientes})}</span>
          {accionFaltanDudas}
        </div>
      )}
    </div>
  )
}
